import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { SalaryInputs, CalculationResult, UKRegion, StudentLoanPlan } from './types/calculator';
import { calculateUKTax } from './utils/taxCalculator';
import { SalaryInputForm } from './components/SalaryInputForm';
import { ResultsSummary } from './components/ResultsSummary';
import { BreakdownTable } from './components/BreakdownTable';
import { AdSlot } from './components/AdSlot';
import { Layout } from './components/Layout';
import { SEOHead } from './components/SEOHead';
import { ROUTES, TAB_TO_PATH } from './routes';
import {
  faqSchema,
  homeFaqs,
  grossToNetFaqs,
  monthlyFaqs,
  nhsFaqs,
  nurseFaqs,
  scottishFaqs,
  ir35Faqs,
  contractorFaqs,
  umbrellaFaqs,
  pensionFaqs,
  reverseFaqs,
  comparisonFaqs,
  taxCodeFaqs,
  selfEmployedFaqs,
  bonusFaqs,
  hourlyToSalaryFaqs,
  proRataFaqs,
  overtimeFaqs,
  minimumWageFaqs,
  dividendTaxFaqs,
  mortgageFaqs,
  stampDutyFaqs,
  childBenefitFaqs,
  statutorySickPayFaqs,
  maternityPayFaqs,
  teacherPayFaqs,
  taxFreeChildcareFaqs,
} from './data/calculatorFaqs';

// Below-the-fold homepage widgets and every non-homepage route are code-split so the
// initial bundle only ships what the current page actually needs (recharts, react-markdown
// and the other tool components were previously all loaded upfront on every page).
const TaxCharts = lazy(() => import('./components/TaxCharts').then((m) => ({ default: m.TaxCharts })));
const MarginalTaxVisualizer = lazy(() => import('./components/MarginalTaxVisualizer').then((m) => ({ default: m.MarginalTaxVisualizer })));
const HomePage = lazy(() => import('./components/HomePage').then((m) => ({ default: m.HomePage })));
const GrossToNetContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.GrossToNetContent })));
const MonthlyContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.MonthlyContent })));
const NHSContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.NHSContent })));
const NurseContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.NurseContent })));
const ScottishContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.ScottishContent })));
const IR35Content = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.IR35Content })));
const ContractorContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.ContractorContent })));
const UmbrellaContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.UmbrellaContent })));
const PensionContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.PensionContent })));
const ReverseContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.ReverseContent })));
const ComparisonContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.ComparisonContent })));
const TaxCodeContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.TaxCodeContent })));
const SelfEmployedContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.SelfEmployedContent })));
const SelfEmployedCalculator = lazy(() => import('./components/SelfEmployedCalculator').then((m) => ({ default: m.SelfEmployedCalculator })));
const BonusContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.BonusContent })));
const BonusCalculator = lazy(() => import('./components/BonusCalculator').then((m) => ({ default: m.BonusCalculator })));
const HourlyToSalaryContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.HourlyToSalaryContent })));
const HourlyToSalaryCalculator = lazy(() => import('./components/HourlyToSalaryCalculator').then((m) => ({ default: m.HourlyToSalaryCalculator })));
const ProRataContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.ProRataContent })));
const ProRataCalculator = lazy(() => import('./components/ProRataCalculator').then((m) => ({ default: m.ProRataCalculator })));
const OvertimeContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.OvertimeContent })));
const OvertimeCalculator = lazy(() => import('./components/OvertimeCalculator').then((m) => ({ default: m.OvertimeCalculator })));
const MinimumWageContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.MinimumWageContent })));
const MinimumWageCalculator = lazy(() => import('./components/MinimumWageCalculator').then((m) => ({ default: m.MinimumWageCalculator })));
const DividendTaxContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.DividendTaxContent })));
const DividendTaxCalculator = lazy(() => import('./components/DividendTaxCalculator').then((m) => ({ default: m.DividendTaxCalculator })));
const MortgageContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.MortgageContent })));
const MortgageCalculator = lazy(() => import('./components/MortgageCalculator').then((m) => ({ default: m.MortgageCalculator })));
const StampDutyContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.StampDutyContent })));
const StampDutyCalculator = lazy(() => import('./components/StampDutyCalculator').then((m) => ({ default: m.StampDutyCalculator })));
const ChildBenefitContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.ChildBenefitContent })));
const ChildBenefitCalculator = lazy(() => import('./components/ChildBenefitCalculator').then((m) => ({ default: m.ChildBenefitCalculator })));
const StatutorySickPayContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.StatutorySickPayContent })));
const StatutorySickPayCalculator = lazy(() => import('./components/StatutorySickPayCalculator').then((m) => ({ default: m.StatutorySickPayCalculator })));
const MaternityPayContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.MaternityPayContent })));
const MaternityPayCalculator = lazy(() => import('./components/MaternityPayCalculator').then((m) => ({ default: m.MaternityPayCalculator })));
const TeacherPayContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.TeacherPayContent })));
const TeacherPayCalculator = lazy(() => import('./components/TeacherPayCalculator').then((m) => ({ default: m.TeacherPayCalculator })));
const TaxFreeChildcareContent = lazy(() => import('./components/CalculatorContent').then((m) => ({ default: m.TaxFreeChildcareContent })));
const TaxFreeChildcareCalculator = lazy(() => import('./components/TaxFreeChildcareCalculator').then((m) => ({ default: m.TaxFreeChildcareCalculator })));
const NHSCalculator = lazy(() => import('./components/NHSCalculator').then((m) => ({ default: m.NHSCalculator })));
const ScottishCalculator = lazy(() => import('./components/ScottishCalculator').then((m) => ({ default: m.ScottishCalculator })));
const IR35Calculator = lazy(() => import('./components/IR35Calculator').then((m) => ({ default: m.IR35Calculator })));
const PensionSacrificeCalculator = lazy(() => import('./components/PensionSacrificeCalculator').then((m) => ({ default: m.PensionSacrificeCalculator })));
const SalaryComparison = lazy(() => import('./components/SalaryComparison').then((m) => ({ default: m.SalaryComparison })));
const ReverseCalculator = lazy(() => import('./components/ReverseCalculator').then((m) => ({ default: m.ReverseCalculator })));
const TaxCodeDecoder = lazy(() => import('./components/TaxCodeDecoder').then((m) => ({ default: m.TaxCodeDecoder })));
const BlogListPage = lazy(() => import('./components/BlogListPage').then((m) => ({ default: m.BlogListPage })));
const BlogPostPage = lazy(() => import('./components/BlogPostPage').then((m) => ({ default: m.BlogPostPage })));
const CategoryPage = lazy(() => import('./components/CategoryPage').then((m) => ({ default: m.CategoryPage })));
const AuthorPage = lazy(() => import('./components/AuthorPage').then((m) => ({ default: m.AuthorPage })));
const AboutUsPage = lazy(() => import('./components/AboutUsPage').then((m) => ({ default: m.AboutUsPage })));
const WriteForUsPage = lazy(() => import('./components/WriteForUsPage').then((m) => ({ default: m.WriteForUsPage })));
const ContactPage = lazy(() => import('./components/ContactPage').then((m) => ({ default: m.ContactPage })));
const PrivacyPolicyPage = lazy(() => import('./components/PrivacyPolicyPage').then((m) => ({ default: m.PrivacyPolicyPage })));
const TermsPage = lazy(() => import('./components/TermsPage').then((m) => ({ default: m.TermsPage })));
const EditorialPolicyPage = lazy(() => import('./components/EditorialPolicyPage').then((m) => ({ default: m.EditorialPolicyPage })));
const NotFoundPage = lazy(() => import('./components/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));

const RouteFallback: React.FC = () => (
  <div className="flex items-center justify-center py-24">
    <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" aria-label="Loading" />
  </div>
);

const DEFAULT_INPUTS: SalaryInputs = {
  grossIncome: 35000,
  incomePeriod: 'year',
  hoursPerWeek: 37.5,
  daysPerWeek: 5,
  taxYear: '2025/26',
  region: 'england_ni_wales',
  taxCode: '1257L',
  isBlind: false,
  marriageAllowance: 'none',
  pensionContribution: 5,
  pensionType: 'percentage',
  pensionMethod: 'salary_sacrifice',
  studentLoanPlan: 'none',
  niCategory: 'A',
  bonus: 0,
  overtimeHoursPerMonth: 0,
  overtimeHourlyRate: 0,
  benefitInKind: 0,
  otherDeductionsMonthly: 0,
};

function readInputsFromSearch(search: string): SalaryInputs {
  const params = new URLSearchParams(search);
  const sal = parseFloat(params.get('salary') || '');
  const code = params.get('taxCode');
  const reg = params.get('region') as UKRegion;
  const pen = parseFloat(params.get('pension') || '');
  const sl = params.get('studentLoan') as StudentLoanPlan;

  return {
    ...DEFAULT_INPUTS,
    grossIncome: isNaN(sal) ? 35000 : sal,
    taxCode: code || '1257L',
    region: reg || 'england_ni_wales',
    pensionContribution: isNaN(pen) ? 5 : pen,
    studentLoanPlan: sl || 'none',
  };
}

const CalculatorPage: React.FC<{
  inputs: SalaryInputs;
  setInputs: React.Dispatch<React.SetStateAction<SalaryInputs>>;
  result: CalculationResult;
}> = ({ inputs, setInputs, result }) => (
  <div className="space-y-8">
    <SEOHead
      path={ROUTES.calculator}
      title="Gross to Net Pay Calculator UK 2025/26 | Take-Home Salary"
      description="Gross to net pay calculator UK 2025/26: convert any salary to exact take-home after Income Tax, National Insurance, pension and student loan. Net to gross also available."
      jsonLd={[
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'UK Take Home Pay Calculator',
          applicationCategory: 'FinanceApplication',
          operatingSystem: 'All',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'GBP' },
        },
        faqSchema(grossToNetFaqs),
      ]}
    />
    <div>
      <h1 className="text-lg font-bold text-white">Gross to Net Pay Calculator UK 2025/26</h1>
      <p className="text-xs text-zinc-400 mt-0.5">
        Convert any UK salary to exact take-home pay after Income Tax, National Insurance, pension and student loan deductions.
      </p>
    </div>
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
      <div className="xl:col-span-5 space-y-6">
        <SalaryInputForm inputs={inputs} onChange={setInputs} />
      </div>
      <div className="xl:col-span-7 space-y-6">
        <ResultsSummary result={result} />
      </div>
    </div>
    <BreakdownTable result={result} />
    <Suspense fallback={<RouteFallback />}>
      <TaxCharts result={result} />
      <MarginalTaxVisualizer result={result} />
      <AdSlot slot="leaderboard" className="w-full" />
      <GrossToNetContent />
    </Suspense>
  </div>
);

const MonthlyCalculatorPage: React.FC<{
  inputs: SalaryInputs;
  setInputs: React.Dispatch<React.SetStateAction<SalaryInputs>>;
  result: CalculationResult;
}> = ({ inputs, setInputs, result }) => (
  <div className="space-y-8">
    <SEOHead
      path={ROUTES.monthly}
      title="Monthly Take Home Pay Calculator UK 2025/26 | Net Salary"
      description="Monthly take home pay calculator UK 2025/26: convert any annual salary to your exact monthly net pay after Income Tax, National Insurance, pension and student loan."
      jsonLd={[
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'Monthly Take Home Pay Calculator UK',
          applicationCategory: 'FinanceApplication',
          operatingSystem: 'All',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'GBP' },
        },
        faqSchema(monthlyFaqs),
      ]}
    />
    <div>
      <h1 className="text-lg font-bold text-white">Monthly Take Home Pay Calculator UK</h1>
      <p className="text-xs text-zinc-400 mt-0.5">
        Convert any annual salary to your exact monthly take-home pay after Income Tax, National Insurance, pension
        and student loan deductions.
      </p>
    </div>
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
      <div className="xl:col-span-5 space-y-6">
        <SalaryInputForm inputs={inputs} onChange={setInputs} />
      </div>
      <div className="xl:col-span-7 space-y-6">
        <ResultsSummary result={result} />
      </div>
    </div>
    <BreakdownTable result={result} />
    <Suspense fallback={<RouteFallback />}>
      <AdSlot slot="leaderboard" className="w-full" />
      <MonthlyContent />
    </Suspense>
  </div>
);

// Old bookmarked/shared links (from when the calculator lived at "/") carry a
// ?salary= param — send those straight to the calculator's new URL instead of
// showing them the homepage with their param silently ignored.
const HomeRoute: React.FC = () => {
  const location = useLocation();
  if (new URLSearchParams(location.search).has('salary')) {
    return <Navigate to={`${ROUTES.calculator}${location.search}`} replace />;
  }
  return <HomePage />;
};

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const [inputs, setInputs] = useState<SalaryInputs>(() =>
    typeof window !== 'undefined' ? readInputsFromSearch(window.location.search) : DEFAULT_INPUTS
  );

  // GA4's default snippet only fires a pageview on first load — since this is a
  // client-side-routed SPA, send one manually whenever the path changes (send_page_view
  // is disabled in index.html to avoid double-counting the initial load).
  useEffect(() => {
    const gtag = (window as any).gtag;
    if (typeof gtag !== 'function') return;
    gtag('event', 'page_view', {
      page_path: location.pathname,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname]);

  // Re-sync inputs from the URL whenever navigation brings us back to the calculator
  // with different query params (e.g. header "Quick £Xk" presets).
  useEffect(() => {
    if (location.pathname === ROUTES.calculator || location.pathname === ROUTES.monthly) {
      setInputs(readInputsFromSearch(location.search));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.pathname]);

  useEffect(() => {
    if (location.pathname !== ROUTES.calculator && location.pathname !== ROUTES.monthly) return;
    const params = new URLSearchParams();
    params.set('salary', inputs.grossIncome.toString());
    params.set('period', inputs.incomePeriod);
    params.set('taxCode', inputs.taxCode);
    params.set('region', inputs.region);
    params.set('pension', inputs.pensionContribution.toString());
    params.set('studentLoan', inputs.studentLoanPlan);
    const newUrl = `${location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs, location.pathname]);

  const result: CalculationResult = calculateUKTax(inputs);

  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route element={<Layout />}>
        <Route path={ROUTES.home} element={<HomeRoute />} />

        <Route
          path={ROUTES.calculator}
          element={<CalculatorPage inputs={inputs} setInputs={setInputs} result={result} />}
        />

        <Route
          path={ROUTES.monthly}
          element={<MonthlyCalculatorPage inputs={inputs} setInputs={setInputs} result={result} />}
        />

        <Route
          path={ROUTES.nhs}
          element={
            <>
              <SEOHead
                path={ROUTES.nhs}
                title="NHS Take Home Pay Calculator 2026/27 | AfC Bands"
                description="NHS take home pay calculator for 2026/27: exact take-home by Agenda for Change band and pay point, with part-time hours, NHS pension tiers and London HCAS."
                jsonLd={faqSchema(nhsFaqs)}
              />
              <NHSCalculator
                onApplyToMain={(salary, pension) => {
                  setInputs((prev) => ({ ...prev, grossIncome: salary, pensionContribution: pension }));
                  navigate(ROUTES.calculator);
                }}
              />
              <AdSlot slot="leaderboard" className="w-full mt-8" />
              <div className="mt-8"><NHSContent /></div>
            </>
          }
        />

        <Route
          path={ROUTES.nurse}
          element={
            <>
              <SEOHead
                path={ROUTES.nurse}
                title="Nurse Take Home Pay Calculator UK 2026/27 | NHS Pay"
                description="Nurse take home pay calculator for 2026/27: exact monthly net pay for Band 5, 6 and 7 nurses, with NHS pension tiers, unsocial hours, part-time and London HCAS."
                jsonLd={faqSchema(nurseFaqs)}
              />
              <NHSCalculator
                headline="Nurse Take Home Pay Calculator"
                subhead="Work out exact monthly take-home pay for registered nurses on NHS Agenda for Change Bands 5, 6 and 7 — including the 2026/27 pay award, NHS pension tiers, unsocial-hours enhancements, part-time contracted hours and London HCAS weighting."
                onApplyToMain={(salary, pension) => {
                  setInputs((prev) => ({ ...prev, grossIncome: salary, pensionContribution: pension }));
                  navigate(ROUTES.calculator);
                }}
              />
              <AdSlot slot="leaderboard" className="w-full mt-8" />
              <div className="mt-8"><NurseContent /></div>
            </>
          }
        />

        <Route
          path={ROUTES.scottish}
          element={
            <>
              <SEOHead
                path={ROUTES.scottish}
                title="Scottish Tax Calculator 2025/26 | Scotland Take-Home Pay"
                description="Scottish tax calculator working out your take-home pay for 2025/26, covering the starter, basic, intermediate, higher and top bands vs the rest of the UK."
                jsonLd={faqSchema(scottishFaqs)}
              />
              <ScottishCalculator />
              <AdSlot slot="leaderboard" className="w-full mt-8" />
              <div className="mt-8"><ScottishContent /></div>
            </>
          }
        />

        <Route
          path={ROUTES.ir35}
          element={
            <>
              <SEOHead
                path={ROUTES.ir35}
                title="IR35 Calculator 2025/26 | Inside vs Outside Take-Home"
                description="IR35 calculator comparing Inside IR35 vs Outside IR35 take-home pay for 2025/26, including umbrella company PAYE deductions vs limited company net income."
                jsonLd={faqSchema(ir35Faqs)}
              />
              <IR35Calculator />
              <AdSlot slot="leaderboard" className="w-full mt-8" />
              <div className="mt-8"><IR35Content /></div>
            </>
          }
        />

        <Route
          path={ROUTES.contractor}
          element={
            <>
              <SEOHead
                path={ROUTES.contractor}
                title="Contractor Take Home Pay Calculator UK 2025/26 | Day Rate"
                description="Contractor take home pay calculator for 2025/26: turn a day or hourly rate into real annual take-home, comparing outside-IR35 limited company vs inside-IR35 umbrella."
                jsonLd={faqSchema(contractorFaqs)}
              />
              <IR35Calculator
                headline="Contractor Take Home Pay Calculator"
                subhead="Turn a day rate or hourly rate into your real annual and monthly take-home pay, comparing an outside-IR35 limited company against an inside-IR35 umbrella arrangement for 2025/26."
                badge="Contractor Day Rate → Take-Home 2025/26"
              />
              <AdSlot slot="leaderboard" className="w-full mt-8" />
              <div className="mt-8"><ContractorContent /></div>
            </>
          }
        />

        <Route
          path={ROUTES.umbrella}
          element={
            <>
              <SEOHead
                path={ROUTES.umbrella}
                title="Umbrella Take Home Pay Calculator UK 2025/26 | PAYE"
                description="Umbrella take home pay calculator for 2025/26: work out your net pay after the umbrella margin, employer NI, Apprenticeship Levy and PAYE Income Tax and National Insurance."
                jsonLd={faqSchema(umbrellaFaqs)}
              />
              <IR35Calculator
                headline="Umbrella Take Home Pay Calculator"
                subhead="Work out what you actually keep when paid through an umbrella company inside IR35 — after the umbrella margin, employer National Insurance, the Apprenticeship Levy and PAYE Income Tax and National Insurance."
                badge="Umbrella Assignment Rate → Take-Home 2025/26"
              />
              <AdSlot slot="leaderboard" className="w-full mt-8" />
              <div className="mt-8"><UmbrellaContent /></div>
            </>
          }
        />

        <Route
          path={ROUTES.pension}
          element={
            <>
              <SEOHead
                path={ROUTES.pension}
                title="Salary Sacrifice Calculator 2025/26 | Pension Tax Savings"
                description="Salary sacrifice calculator showing how much Income Tax and National Insurance a pension scheme saves you in 2025/26, with side-by-side take-home comparisons."
                jsonLd={faqSchema(pensionFaqs)}
              />
              <PensionSacrificeCalculator />
              <AdSlot slot="leaderboard" className="w-full mt-8" />
              <div className="mt-8"><PensionContent /></div>
            </>
          }
        />

        <Route
          path={ROUTES.reverse}
          element={
            <>
              <SEOHead
                path={ROUTES.reverse}
                title="Net to Gross Salary Calculator UK 2025/26"
                description="Net to gross salary calculator: work backwards from a target take-home pay to the gross salary you need to ask for, under 2025/26 UK tax, NI and pension rules."
                jsonLd={faqSchema(reverseFaqs)}
              />
              <ReverseCalculator baseInputs={inputs} />
              <AdSlot slot="leaderboard" className="w-full mt-8" />
              <div className="mt-8"><ReverseContent /></div>
            </>
          }
        />

        <Route
          path={ROUTES.comparison}
          element={
            <>
              <SEOHead
                path={ROUTES.comparison}
                title="Compare Job Offers by Salary | UK Take-Home Pay"
                description="Compare two UK job offers side by side by exact take-home pay, not just headline salary, including tax, NI, pension and student loan differences."
                jsonLd={faqSchema(comparisonFaqs)}
              />
              <SalaryComparison initialInputs={inputs} />
              <AdSlot slot="leaderboard" className="w-full mt-8" />
              <div className="mt-8"><ComparisonContent /></div>
            </>
          }
        />

        <Route
          path={ROUTES.taxcode}
          element={
            <>
              <SEOHead
                path={ROUTES.taxcode}
                title="UK Tax Code Decoder 2025/26 | What Does Your Code Mean?"
                description="UK tax code decoder that explains any code instantly, including 1257L, BR, K codes, 0T, D0, D1, NT and Scottish S-prefix codes, and what each means for pay."
                jsonLd={faqSchema(taxCodeFaqs)}
              />
              <TaxCodeDecoder />
              <AdSlot slot="leaderboard" className="w-full mt-8" />
              <div className="mt-8"><TaxCodeContent /></div>
            </>
          }
        />

        <Route
          path={ROUTES.selfEmployed}
          element={
            <>
              <SEOHead
                path={ROUTES.selfEmployed}
                title="Self-Employed Tax Calculator 2025/26 | Sole Trader Take-Home"
                description="Self-employed tax calculator for 2025/26: Income Tax, Class 4 National Insurance, the £1,000 Trading Allowance, and how much to set aside for your tax bill."
                jsonLd={faqSchema(selfEmployedFaqs)}
              />
              <SelfEmployedCalculator />
              <AdSlot slot="leaderboard" className="w-full mt-8" />
              <div className="mt-8"><SelfEmployedContent /></div>
            </>
          }
        />

        <Route
          path={ROUTES.bonus}
          element={
            <>
              <SEOHead
                path={ROUTES.bonus}
                title="Bonus Tax Calculator UK 2025/26 | What You Keep"
                description="Bonus tax calculator showing how much of a bonus payment you keep after Income Tax and National Insurance in 2025/26, including the real marginal-rate impact."
                jsonLd={faqSchema(bonusFaqs)}
              />
              <BonusCalculator />
              <AdSlot slot="leaderboard" className="w-full mt-8" />
              <div className="mt-8"><BonusContent /></div>
            </>
          }
        />

        <Route
          path={ROUTES.hourlyToSalary}
          element={
            <>
              <SEOHead
                path={ROUTES.hourlyToSalary}
                title="Hourly to Salary Calculator UK 2025/26 | Take-Home Pay"
                description="Hourly to salary calculator that converts an hourly rate into an annual salary and shows your take-home pay after Income Tax and National Insurance for 2025/26."
                jsonLd={faqSchema(hourlyToSalaryFaqs)}
              />
              <HourlyToSalaryCalculator />
              <AdSlot slot="leaderboard" className="w-full mt-8" />
              <div className="mt-8"><HourlyToSalaryContent /></div>
            </>
          }
        />

        <Route
          path={ROUTES.proRata}
          element={
            <>
              <SEOHead
                path={ROUTES.proRata}
                title="Pro Rata Salary Calculator UK 2025/26 | Take-Home Pay"
                description="Pro rata salary calculator working out a part-time salary from a full-time equivalent figure, plus your take-home pay after Income Tax and NI for 2025/26."
                jsonLd={faqSchema(proRataFaqs)}
              />
              <ProRataCalculator />
              <AdSlot slot="leaderboard" className="w-full mt-8" />
              <div className="mt-8"><ProRataContent /></div>
            </>
          }
        />

        <Route
          path={ROUTES.overtime}
          element={
            <>
              <SEOHead
                path={ROUTES.overtime}
                title="Overtime Pay Calculator UK 2025/26 | Extra Take-Home"
                description="Overtime pay calculator showing exactly how much extra take-home pay your overtime hours add, after Income Tax and National Insurance deductions for 2025/26."
                jsonLd={faqSchema(overtimeFaqs)}
              />
              <OvertimeCalculator />
              <AdSlot slot="leaderboard" className="w-full mt-8" />
              <div className="mt-8"><OvertimeContent /></div>
            </>
          }
        />

        <Route
          path={ROUTES.minimumWage}
          element={
            <>
              <SEOHead
                path={ROUTES.minimumWage}
                title="Minimum Wage Calculator UK 2025/26 | NLW & NMW"
                description="Minimum wage calculator working out take-home pay on the National Living Wage or National Minimum Wage for 2025/26, broken down by age band and hours worked."
                jsonLd={faqSchema(minimumWageFaqs)}
              />
              <MinimumWageCalculator />
              <AdSlot slot="leaderboard" className="w-full mt-8" />
              <div className="mt-8"><MinimumWageContent /></div>
            </>
          }
        />

        <Route
          path={ROUTES.dividendTax}
          element={
            <>
              <SEOHead
                path={ROUTES.dividendTax}
                title="Dividend Tax Calculator UK 2025/26 | Dividend Allowance"
                description="Dividend tax calculator for 2025/26 covering the £500 Dividend Allowance and the 8.75%, 33.75% and 39.35% dividend tax band rates on top of your other income."
                jsonLd={faqSchema(dividendTaxFaqs)}
              />
              <DividendTaxCalculator />
              <AdSlot slot="leaderboard" className="w-full mt-8" />
              <div className="mt-8"><DividendTaxContent /></div>
            </>
          }
        />

        <Route
          path={ROUTES.mortgage}
          element={
            <>
              <SEOHead
                path={ROUTES.mortgage}
                title="Mortgage Repayment Calculator UK 2025/26 | Monthly Cost"
                description="Mortgage repayment calculator working out your monthly repayment and total interest paid over the term, for both repayment and interest-only UK mortgages."
                jsonLd={faqSchema(mortgageFaqs)}
              />
              <MortgageCalculator />
              <AdSlot slot="leaderboard" className="w-full mt-8" />
              <div className="mt-8"><MortgageContent /></div>
            </>
          }
        />

        <Route
          path={ROUTES.stampDuty}
          element={
            <>
              <SEOHead
                path={ROUTES.stampDuty}
                title="Stamp Duty Calculator UK 2025/26 | SDLT for England"
                description="Stamp duty calculator for England and Northern Ireland 2025/26: work out SDLT for home movers, first-time buyers, and additional properties like buy-to-let."
                jsonLd={faqSchema(stampDutyFaqs)}
              />
              <StampDutyCalculator />
              <AdSlot slot="leaderboard" className="w-full mt-8" />
              <div className="mt-8"><StampDutyContent /></div>
            </>
          }
        />

        <Route
          path={ROUTES.childBenefit}
          element={
            <>
              <SEOHead
                path={ROUTES.childBenefit}
                title="Child Benefit Calculator UK 2025/26 | HICBC Charge"
                description="Child benefit calculator for 2025/26: work out your High Income Child Benefit Charge (HICBC) between £60,000 and £80,000, and how much you keep."
                jsonLd={faqSchema(childBenefitFaqs)}
              />
              <ChildBenefitCalculator />
              <AdSlot slot="leaderboard" className="w-full mt-8" />
              <div className="mt-8"><ChildBenefitContent /></div>
            </>
          }
        />

        <Route
          path={ROUTES.statutorySickPay}
          element={
            <>
              <SEOHead
                path={ROUTES.statutorySickPay}
                title="Statutory Sick Pay Calculator UK 2025/26 | SSP Rate"
                description="Statutory Sick Pay calculator for 2025/26: work out your SSP at £118.75 a week after the 3 unpaid waiting days, and check if you are eligible."
                jsonLd={faqSchema(statutorySickPayFaqs)}
              />
              <StatutorySickPayCalculator />
              <AdSlot slot="leaderboard" className="w-full mt-8" />
              <div className="mt-8"><StatutorySickPayContent /></div>
            </>
          }
        />

        <Route
          path={ROUTES.maternityPay}
          element={
            <>
              <SEOHead
                path={ROUTES.maternityPay}
                title="Maternity Pay Calculator UK 2025/26 | SMP Rate"
                description="Maternity pay calculator for 2025/26: work out your Statutory Maternity Pay across the full 39 weeks, including the higher first-6-weeks rate."
                jsonLd={faqSchema(maternityPayFaqs)}
              />
              <MaternityPayCalculator />
              <AdSlot slot="leaderboard" className="w-full mt-8" />
              <div className="mt-8"><MaternityPayContent /></div>
            </>
          }
        />

        <Route
          path={ROUTES.teacherPay}
          element={
            <>
              <SEOHead
                path={ROUTES.teacherPay}
                title="Teacher Take Home Pay Calculator UK 2025/26"
                description="Teacher take home pay calculator for UK 2025/26: exact pay for Main and Upper Pay Range points, using official STPCD scales and TPS pension tiers."
                jsonLd={faqSchema(teacherPayFaqs)}
              />
              <TeacherPayCalculator />
              <AdSlot slot="leaderboard" className="w-full mt-8" />
              <div className="mt-8"><TeacherPayContent /></div>
            </>
          }
        />

        <Route
          path={ROUTES.taxFreeChildcare}
          element={
            <>
              <SEOHead
                path={ROUTES.taxFreeChildcare}
                title="Tax-Free Childcare Calculator UK 2025/26 | Top-Up"
                description="Tax-free childcare calculator: work out your government top-up, £2 for every £8 you pay in, up to £500 a quarter per child (£1,000 if disabled)."
                jsonLd={faqSchema(taxFreeChildcareFaqs)}
              />
              <TaxFreeChildcareCalculator />
              <AdSlot slot="leaderboard" className="w-full mt-8" />
              <div className="mt-8"><TaxFreeChildcareContent /></div>
            </>
          }
        />

        <Route path={ROUTES.blog} element={<BlogListPage />} />
        <Route path="/category/:categorySlug" element={<CategoryPage />} />
        <Route path="/authors/:authorSlug" element={<AuthorPage />} />

        <Route
          path={ROUTES.about}
          element={
            <>
              <SEOHead
                path={ROUTES.about}
                title="About Us | Take Home Pay Calculators UK Team & Standards"
                description="About Take Home Pay Calculators UK: meet the team behind our HMRC-aligned tax algorithms, and learn how our editorial standards and data sources actually work."
              />
              <AboutUsPage onNavigateToTab={(tab) => navigate(TAB_TO_PATH[tab] || '/')} />
            </>
          }
        />
        <Route
          path={ROUTES.writeForUs}
          element={
            <>
              <SEOHead
                path={ROUTES.writeForUs}
                title="Write For Us | UK Tax & Salary Guest Posts"
                description="Write for us: contribute a UK tax, payroll or salary guide to Take Home Pay Calculators, using our editorial guidelines and guest submission process."
              />
              <WriteForUsPage onNavigateToTab={(tab) => navigate(TAB_TO_PATH[tab] || '/')} />
            </>
          }
        />
        <Route
          path={ROUTES.contact}
          element={
            <>
              <SEOHead
                path={ROUTES.contact}
                title="Contact Us | Take Home Pay Calculators UK"
                description="Contact Take Home Pay Calculators UK for corrections to our tax figures, data queries, partnership enquiries, press requests or general feedback."
              />
              <ContactPage onNavigateToTab={(tab) => navigate(TAB_TO_PATH[tab] || '/')} />
            </>
          }
        />
        <Route
          path={ROUTES.privacy}
          element={
            <>
              <SEOHead
                path={ROUTES.privacy}
                title="Privacy Policy | Take Home Pay Calculators UK"
                description="Privacy policy for Take Home Pay Calculators UK, explaining how we handle data, cookies, analytics tracking and third-party advertising across the whole site."
              />
              <PrivacyPolicyPage onNavigateToTab={(tab) => navigate(TAB_TO_PATH[tab] || '/')} />
            </>
          }
        />
        <Route
          path={ROUTES.terms}
          element={
            <>
              <SEOHead
                path={ROUTES.terms}
                title="Terms & Conditions | Take Home Pay Calculators UK"
                description="Terms and conditions for using Take Home Pay Calculators UK, covering disclaimers on calculator accuracy, acceptable use, liability and site content rules."
              />
              <TermsPage onNavigateToTab={(tab) => navigate(TAB_TO_PATH[tab] || '/')} />
            </>
          }
        />
        <Route
          path={ROUTES.editorialPolicy}
          element={
            <>
              <SEOHead
                path={ROUTES.editorialPolicy}
                title="Editorial Policy | Take Home Pay Calculators UK"
                description="Editorial policy for Take Home Pay Calculators UK: how we research, fact-check and update every UK tax guide, including sourcing standards and review cadence."
              />
              <EditorialPolicyPage />
            </>
          }
        />

        {/* Flat blog-post slugs must stay last so they don't shadow the routes above */}
        <Route path="/:slug" element={<BlogPostPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
    </Suspense>
  );
}
